// @flow

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import '@react/react-spectrum/page';
import Button from '@react/react-spectrum/Button';
import Banner from '@react/react-spectrum/Banner';
import Select from '@react/react-spectrum/Select';
import Link from '@react/react-spectrum/Link';
import AdobeExperienceCloud from '@react/react-spectrum/Icon/AdobeExperienceCloud';
import Wait from '@react/react-spectrum/Wait';
import { withLocalization } from '@exc-ui/exc-sdk/dist/production/localization/withLocalization';
import Background from '../Background/Background';
import TermsOfUseDialogWrapper from '../TermsOfUseDialog/TermsOfUseDialogWrapper';
import SolutionsDropdownWrapper from '../SolutionsDropdown/SolutionsDropdownWrapper';
import './ExpLogin.css';
import { initializeEnvironmentVariables } from '../../utils/envUtil';
import { getOrDefaultQueryParameter, isBlankString, loadScript } from '../../utils/commonUtil';
import { handleIframePostMessage } from './utils/loginUtil';
import experiencelogo from './resources/experience-logo.png';
import { PARAM_EXC_LOGIN, NOOP } from '../../constants/genericConstants';
import * as excLoginConstants from './constants/loginConstants';
import AdobeExCIcon from './constants/AdobeExCIcon';

class ExpLogin extends Component {
  static propTypes = {
    /** Experience Cloud Domain*/
    excDomian: PropTypes.string,
    /** Terms of Use page Link*/
    touHref: PropTypes.string,
    /** Localization Strings*/
    localizationStrings: PropTypes.shape({
      /** Sign In Button Text*/
      signInButtonText: PropTypes.string,
      /** privacy*/
      privacy: PropTypes.string,
      /** updated*/
      updated: PropTypes.string,
      /** Terms of use*/
      termsOfUse: PropTypes.string,
      /** cookies*/
      cookies: PropTypes.string,
      /** help*/
      help: PropTypes.string
    }),
    /** i18n FormattedMessage*/
    FormattedMessage: PropTypes.func.isRequired
  };

  static defaultProps = {
    excDomian: 'https://experiencecloud.adobe.com',
    touHref: '',
    localizationStrings: {
      signInButtonText: 'Sign In with an Adobe ID',
      privacy: 'Privacy',
      updated: 'UPDATED',
      termsOfUse: 'Terms of Use',
      cookies: 'Cookies',
      help: 'Help'
    },
    FormattedMessage: NOOP
  };

  constructor(props) {
    super(props);
    this.isOpenedInIframe = window.self !== window.top ? true : false;
    this.envVar = initializeEnvironmentVariables();
    this.paramRedirectLocation = getOrDefaultQueryParameter(excLoginConstants.PARAM_REDIRECT_LOCATION, window.location.search, document.referrer);
    this.resource = getOrDefaultQueryParameter(excLoginConstants.PARAM_RESOURCE, window.location.search, '');
    this.prefixtenantid = this.envVar.TENANT_ID;
    const queryParamTenantId = getOrDefaultQueryParameter(excLoginConstants.PARAM_PREFIX_TENANT_ID, window.location.search, '');
    if (
      !isBlankString(queryParamTenantId) &&
      !queryParamTenantId.startsWith(PARAM_EXC_LOGIN) &&
      !queryParamTenantId.startsWith(excLoginConstants.PARAM_EXC_LOGINIMS)
    ) {
      this.prefixtenantid = queryParamTenantId;
    }

    this.redirecturi =
      this.envVar.LOGIN_SERVICE_IMS_ENDPOINT +
      this.envVar.EXC_SESSION_IMSLOGIN_URL +
      '?provider=' +
      this.envVar.IMS_CLIENT_ID +
      '&prefixtenantid=' +
      this.prefixtenantid +
      '&redirectlocation=' +
      encodeURIComponent(this.paramRedirectLocation) +
      '&resource=' +
      encodeURIComponent(this.resource);
    this.redirecturi = encodeURIComponent(this.redirecturi);
  }

  state = {
    loading: 'true',
    enableTOUComponent: false,
    touHref: ''
  };

  loginServiceImsSSO = (accessToken, expiryTime, loginAs) => {
    let imsSSOHandler = this.envVar.LOGIN_SERVICE_IMS_ENDPOINT + this.envVar.EXC_SESSION_IMSSSO_HANDLER;
    if (loginAs) {
      imsSSOHandler = imsSSOHandler.replace(excLoginConstants.PARAM_EXC_LOGINIMS, this.prefixtenantid);
    }
    axios
      .get(imsSSOHandler, {
        params: {
          'ims-accesstoken': accessToken,
          'expiry-time-interval': expiryTime,
          prefixtenantid: this.prefixtenantid,
          redirectlocation: this.paramRedirectLocation,
          resource: this.resource,
          loginas: loginAs
        },
        withCredentials: true
      })
      .then(response => {
        this.setState({ loading: 'false' });
        handleIframePostMessage(this.isOpenedInIframe, true, null, response.data.redirectLocation);
        if (!this.isOpenedInIframe && response.data.redirectLocation) {
          window.location.href = response.data.redirectLocation;
        }
      })
      .catch(error => {
        this.setState({ loading: 'false' });
        handleIframePostMessage(this.isOpenedInIframe, false, error, null);
        console.log(error);
      });
  };

  async componentDidMount() {
    try {
      await loadScript(this.envVar.OMEGA_SCRIPT_SRC);
    } catch (error) {
      console.error('Omega script load failed:', error);
    }

    let loginServiceSessionUrl = this.envVar.LOGIN_SERVICE_ENDPOINT + this.envVar.EXC_SESSION_VALIDATE_URL;
    axios
      .get(loginServiceSessionUrl, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        withCredentials: true
      })
      .then(response => {
        if (response.data.valid === true) {
          if (this.prefixtenantid !== '') {
            this.envVar.EXC_LOGIN_URL = this.envVar.EXC_LOGIN_URL + '?prefixtenantid=' + this.prefixtenantid;
          }
          this.setState({ loading: 'false' });
          handleIframePostMessage(this.isOpenedInIframe, true, null, this.envVar.EXC_LOGIN_URL);
          if (!this.isOpenedInIframe) {
            window.location.href = this.envVar.EXC_LOGIN_URL;
          }
        } else {
          let aauiAccessToken = window.location.hash.match(excLoginConstants.ACCESS_TOKEN_REGEX);

          /** JIRA BUG ID: EXC-807*/
          if (
            /(loginas=false|loginas%3Dfalse|loginas%253Dfalse)/i.test(this.resource) ||
            /(loginas=false|loginas%3Dfalse|loginas%253Dfalse)/i.test(this.paramRedirectLocation)
          ) {
            aauiAccessToken = null;
          }
          if (aauiAccessToken && !isBlankString(aauiAccessToken[1])) {
            this.prefixtenantid = getOrDefaultQueryParameter(excLoginConstants.PARAM_HINT_TENANT_ID, window.location.search, this.prefixtenantid);
            this.loginServiceImsSSO(aauiAccessToken[1], excLoginConstants.EXPIRY_TIME, true);
          } else {
            let imsvalidateurl =
              this.envVar.IMS_SERVER_ENDPOINT +
              '/ims/check/v3/token?client_id=' +
              this.envVar.IMS_CLIENT_ID +
              '&scope=' +
              excLoginConstants.IMS_SCOPE_PARAMS_SUSI_LOGIN;
            axios
              .get(imsvalidateurl, {
                headers: {
                  'X-IMS-ClientId': this.envVar.IMS_CLIENT_ID
                },
                withCredentials: true
              })
              .then(response => {
                if (response.data.access_token !== undefined) {
                  this.loginServiceImsSSO(response.data.access_token, response.data.expires_in, false);
                } else {
                  this.setState({ loading: 'false' });
                  handleIframePostMessage(this.isOpenedInIframe, false, response.data.error, null);
                }
              })
              .catch(error => {
                const imsErrorResponse = error.response.data;

                if (this.isOpenedInIframe && imsErrorResponse && imsErrorResponse.error === excLoginConstants.IMS_TOU_ERROR) {
                  this.setState({
                    loading: 'false',
                    enableTOUComponent: true,
                    touHref: imsErrorResponse.jump
                  });
                  if (!window.location.pathname.includes('/exc-content/login.html')) {
                    handleIframePostMessage(this.isOpenedInIframe, true, null, this.envVar.EXC_LOGIN_URL);
                  }
                } else {
                  this.setState({ loading: 'false' });
                  handleIframePostMessage(this.isOpenedInIframe, false, error, null);
                }
                console.log(error);
              });
          }
        }
      })
      .catch(error => {
        this.setState({ loading: 'false' });
        handleIframePostMessage(this.isOpenedInIframe, false, error, null);
        console.log(error);
      });
    _satellite.pageBottom();
  }

  render() {
    const { localizationStrings, FormattedMessage } = this.props;
    const i18nFM = FormattedMessage;
    const { loading, enableTOUComponent, touHref } = this.state;
    let imsredirecturl =
      this.envVar.IMS_SERVER_ENDPOINT +
      '/ims/authorize/v1?client_id=' +
      this.envVar.IMS_CLIENT_ID +
      '&redirect_uri=' +
      this.redirecturi +
      '&response_type=code&scope=' +
      excLoginConstants.IMS_SCOPE_PARAMS_SUSI_LOGIN;
    let waitlogin;
    if (loading === 'true') {
      waitlogin = (
        <div className="wait-container">
          <Wait size="L" centered />
        </div>
      );
    } else {
      waitlogin = <div />;
    }

    if (this.isOpenedInIframe) {
      if (enableTOUComponent) {
        return (
          <div>
            <Background className="login" />
            <TermsOfUseDialogWrapper excDomian={this.envVar.EXC_SESSION_DOMAIN} touHref={touHref} />
          </div>
        );
      } else {
        return <div />;
      }
    }
    return (
      <div>
        <Background className="login" /> {waitlogin}
        <div id="mac_loginpage" className="mboxDefault" />
        <div className="login-box">
          <div className="leftbox">
            <div id="adobeSignInDetails">
              <AdobeExCIcon className="expcloud-icon" size="L" />
              <h1 className="login-title">Adobe Experience Cloud</h1>
              <div className="sign-in-adobeid">
                <div className="sign-in">
                  <div>
                    <Link href={imsredirecturl}>
                      <Button
                        className="sign-in-button"
                        label={i18nFM({
                          messageId: 'signInButtonText',
                          defaultMessage: localizationStrings.signInButtonText
                        })}
                        variant="cta"
                      />
                    </Link>
                  </div>
                  <p className="or-spacer">
                    <span className="divider leftDivider" />or<span className="divider rightDivider" />
                  </p>
                  <SolutionsDropdownWrapper />
                </div>
              </div>
            </div>
          </div>
        </div>
        <ul className="usage-box">
          <li className="left-li">
            <a className="link" href="http://www.adobe.com/go/marketingcloud_privacy">
              {i18nFM({ messageId: 'privacy', defaultMessage: localizationStrings.privacy })} (
              {i18nFM({ messageId: 'updated', defaultMessage: localizationStrings.updated })}
              )
            </a>
          </li>
          <li className="left-li">
            <a className="link" href="https://marketing.adobe.com/resources/help/en_US/terms.html">
              {i18nFM({ messageId: 'termsOfUse', defaultMessage: localizationStrings.termsOfUse })}
            </a>
          </li>
          <li className="left-li">
            <a className="link" href="http://www.adobe.com/in/privacy/cookies.html">
              {i18nFM({ messageId: 'cookies', defaultMessage: localizationStrings.cookies })}
            </a>
          </li>
          <li className="left-li">
            <a className="link" href="https://helpx.adobe.com/marketing-cloud.html">
              {i18nFM({ messageId: 'help', defaultMessage: localizationStrings.help })}
            </a>
          </li>
          <li className="right-li">© 2018 Adobe Systems Incorporated. All Rights Reserved.</li>
        </ul>
      </div>
    );
  }
}

export default withLocalization(ExpLogin);



// WEBPACK FOOTER //
// ./src/components/ExpLogin/ExpLogin.js